<template>
  <div class="card holiday-approval-card">
    <div class="card-content">
      <div class="holiday-approval-header">
        <div>
          <span class="card-title" style="font-weight: 900"> Holiday Requests </span>

          <div class="grey-text">
            Pending requests for <b>{{ displayVenueName }}</b>
          </div>
        </div>
      </div>

      <div v-if="!activeVenueId" class="card-panel red lighten-5 red-text text-darken-3">
        No venue selected. Please select a venue from the venue selector.
      </div>

      <div v-if="error" class="card-panel red lighten-5 red-text text-darken-3">
        {{ error }}
      </div>

      <p v-if="loading" class="grey-text">Loading holiday requests…</p>

      <div
        v-else-if="activeVenueId && requests.length === 0"
        class="grey-text"
        style="margin-top: 14px"
      >
        No pending holiday requests for this venue.
      </div>

      <div v-else-if="activeVenueId" class="table-wrap holiday-requests-table-wrap">
        <table class="striped core-table holiday-requests-table" style="margin-top: 14px">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Request Type</th>
              <th>Dates</th>
              <th>Days</th>
              <th>Notes</th>
              <th class="center-align">Approve?</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="r in requests" :key="r.id">
              <td>
                <b>{{ r.staffName || "—" }}</b>

                <div class="grey-text" style="font-size: 12px">
                  {{ r.requesterEmail || "" }}
                </div>
              </td>

              <td>
                <span
                  class="core-badge"
                  :class="{
                    'core-badge--success':
                      r.requestType === 'paid_holiday' || r.isPaidHoliday === true,

                    'core-badge--warning':
                      r.requestType === 'unpaid_leave' || r.isUnpaidLeave === true,

                    'core-badge--neutral': !(
                      r.requestType === 'paid_holiday' ||
                      r.isPaidHoliday === true ||
                      r.requestType === 'unpaid_leave' ||
                      r.isUnpaidLeave === true
                    ),
                  }"
                >
                  {{ r.requestTypeLabel || fallbackRequestTypeLabel(r) }}
                </span>
              </td>

              <td>{{ r.startDate }} to {{ r.endDate }}</td>

              <td>
                {{ r.totalDays || (r.dates || []).length || "—" }}
              </td>

              <td>
                {{ r.notes || "—" }}
              </td>

              <td class="center-align approval-cell">
                <div class="approval-options">
                  <label class="approval-option">
                    <input
                      type="checkbox"
                      class="filled-in"
                      :checked="decisionById[r.id] === 'yes'"
                      :disabled="busyId === r.id"
                      @change="setDecision(r, 'yes')"
                    />

                    <span>Yes</span>
                  </label>

                  <label class="approval-option">
                    <input
                      type="checkbox"
                      class="filled-in"
                      :checked="decisionById[r.id] === 'no'"
                      :disabled="busyId === r.id"
                      @change="setDecision(r, 'no')"
                    />

                    <span>No</span>
                  </label>
                </div>

                <div v-if="busyId === r.id" class="grey-text saving-text">Saving…</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!--
      <details style="margin-top: 14px">
        <summary class="grey-text">Debug</summary>
        <pre class="grey-text" style="white-space: pre-wrap">{{ debug }}</pre>
      </details>
      -->
    </div>
  </div>
</template>

<script>
import { auth, db } from "@/firebase";

import { collection, doc, onSnapshot } from "firebase/firestore";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import {
  approveHolidayRequest,
  rejectHolidayRequest,
} from "@/scripts/holidayRequestsApi";

function clean(s) {
  return String(s ?? "").trim();
}

function timestampToMillis(value) {
  if (!value) {
    return 0;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  const parsed = Date.parse(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

function isPendingHolidayRequest(request) {
  const status = clean(
    request.status || request.requestStatus || request.approvalStatus || ""
  ).toLowerCase();

  if (!status) {
    return true;
  }

  return ["pending", "submitted", "awaiting_approval", "awaiting approval"].includes(
    status
  );
}

export default {
  name: "HolidayApprovalPanel",

  props: {
    venueId: {
      type: String,
      default: "",
    },

    venueName: {
      type: String,
      default: "",
    },
  },

  setup() {
    const { venueId } = useVenueContext();

    return {
      contextVenueId: venueId,
    };
  },

  data() {
    return {
      loading: false,
      busyId: "",
      error: "",
      requests: [],
      decisionById: {},
      lastLoadedVenueId: "",
      internalVenueName: "",
      unsubVenue: null,
      unsubRequests: null,
    };
  },

  computed: {
    activeVenueId() {
      return clean(this.contextVenueId || this.venueId || "");
    },

    displayVenueName() {
      return this.internalVenueName || this.venueName || this.activeVenueId || "—";
    },

    debug() {
      return JSON.stringify(
        {
          source: "live Firestore onSnapshot with useVenueContext().venueId fallback",

          activeVenueId: this.activeVenueId,

          contextVenueId: clean(this.contextVenueId || ""),

          propVenueId: clean(this.venueId || ""),

          displayVenueName: this.displayVenueName,

          lastLoadedVenueId: this.lastLoadedVenueId,

          requestCount: this.requests.length,

          decisions: this.decisionById,

          sample: this.requests[0] || null,
        },
        null,
        2
      );
    },
  },

  watch: {
    activeVenueId: {
      immediate: true,

      handler(newVenueId, oldVenueId) {
        this.attachVenueNameListener(newVenueId);

        if (!newVenueId) {
          this.detachRequestsListener();

          this.requests = [];
          this.decisionById = {};
          this.lastLoadedVenueId = "";

          return;
        }

        if (newVenueId === oldVenueId && this.lastLoadedVenueId === newVenueId) {
          return;
        }

        this.attachRequestsListener(newVenueId);
      },
    },
  },

  beforeUnmount() {
    if (typeof this.unsubVenue === "function") {
      this.unsubVenue();
    }

    this.unsubVenue = null;

    this.detachRequestsListener();
  },

  methods: {
    detachRequestsListener() {
      if (typeof this.unsubRequests === "function") {
        this.unsubRequests();
      }

      this.unsubRequests = null;
    },

    attachVenueNameListener(venueId) {
      if (typeof this.unsubVenue === "function") {
        this.unsubVenue();
      }

      this.unsubVenue = null;
      this.internalVenueName = "";

      const cleanVenueId = clean(venueId);

      if (!cleanVenueId) {
        return;
      }

      this.unsubVenue = onSnapshot(
        doc(db, "venues", cleanVenueId),

        (snap) => {
          if (!snap.exists()) {
            this.internalVenueName = cleanVenueId;

            return;
          }

          const data = snap.data() || {};

          this.internalVenueName = clean(
            data.venue_name || data.venueName || data.name || data.label || cleanVenueId
          );
        },

        (err) => {
          console.warn("[HolidayApprovalPanel] venue name snapshot failed:", err);

          this.internalVenueName = cleanVenueId;
        }
      );
    },

    attachRequestsListener(venueId) {
      const cleanVenueId = clean(venueId);

      this.detachRequestsListener();

      if (!cleanVenueId) {
        this.requests = [];
        this.decisionById = {};
        this.lastLoadedVenueId = "";

        return;
      }

      this.loading = true;
      this.error = "";
      this.requests = [];
      this.decisionById = {};
      this.lastLoadedVenueId = cleanVenueId;

      const requestsRef = collection(db, "venues", cleanVenueId, "holiday_requests");

      this.unsubRequests = onSnapshot(
        requestsRef,

        (snapshot) => {
          const liveRequests = snapshot.docs
            .map((requestDoc) => {
              const data = requestDoc.data() || {};

              return {
                id: requestDoc.id,

                ...data,
              };
            })
            .filter(isPendingHolidayRequest)
            .sort((a, b) => {
              const aTime = timestampToMillis(
                a.createdAt ||
                  a.created_at ||
                  a.submittedAt ||
                  a.submitted_at ||
                  a.updatedAt ||
                  a.updated_at
              );

              const bTime = timestampToMillis(
                b.createdAt ||
                  b.created_at ||
                  b.submittedAt ||
                  b.submitted_at ||
                  b.updatedAt ||
                  b.updated_at
              );

              return bTime - aTime;
            });

          this.requests = liveRequests;

          const liveIds = new Set(liveRequests.map((request) => request.id));

          const nextDecisions = {};

          Object.entries(this.decisionById || {}).forEach(([requestId, decision]) => {
            if (liveIds.has(requestId)) {
              nextDecisions[requestId] = decision;
            }
          });

          this.decisionById = nextDecisions;

          this.loading = false;
          this.error = "";
        },

        (err) => {
          console.error("[HolidayApprovalPanel] holiday request snapshot failed:", err);

          this.error = err?.message || String(err);

          this.requests = [];
          this.decisionById = {};
          this.loading = false;
        }
      );
    },

    fallbackRequestTypeLabel(request) {
      if (request?.isPaidHoliday === true || request?.requestType === "paid_holiday") {
        return "Paid Holiday";
      }

      if (request?.isUnpaidLeave === true || request?.requestType === "unpaid_leave") {
        return "Unpaid Leave";
      }

      return "—";
    },

    loadRequests() {
      this.attachRequestsListener(this.activeVenueId);
    },

    async setDecision(request, decision) {
      if (!request?.id || this.busyId) {
        return;
      }

      this.decisionById = {
        ...this.decisionById,

        [request.id]: decision,
      };

      if (decision === "yes") {
        await this.approve(request);

        return;
      }

      if (decision === "no") {
        await this.reject(request);
      }
    },

    async approve(request) {
      const venueId = this.activeVenueId;

      if (!venueId) {
        this.error = "No venue selected.";

        return;
      }

      this.busyId = request.id;

      this.error = "";

      try {
        const user = auth.currentUser;

        await approveHolidayRequest({
          venueId,
          request,

          approverUid: user?.uid || "",

          approverEmail: user?.email || "",
        });
      } catch (e) {
        this.error = e?.message || String(e);

        this.decisionById = {
          ...this.decisionById,

          [request.id]: "",
        };
      } finally {
        this.busyId = "";
      }
    },

    async reject(request) {
      const venueId = this.activeVenueId;

      if (!venueId) {
        this.error = "No venue selected.";

        return;
      }

      const reason = window.prompt("Reason for rejection?") || "";

      this.busyId = request.id;

      this.error = "";

      try {
        const user = auth.currentUser;

        await rejectHolidayRequest({
          venueId,

          requestId: request.id,

          approverUid: user?.uid || "",

          approverEmail: user?.email || "",

          reason,
        });
      } catch (e) {
        this.error = e?.message || String(e);

        this.decisionById = {
          ...this.decisionById,

          [request.id]: "",
        };
      } finally {
        this.busyId = "";
      }
    },
  },
};
</script>

<style scoped>
.holiday-approval-card {
  overflow: hidden;
}

.holiday-approval-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.approval-cell {
  white-space: nowrap;
  min-width: 150px;
}

.approval-options {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.approval-option {
  display: inline-flex;
  align-items: center;
  margin: 0;
}

.approval-option span {
  padding-left: 28px !important;
}

.saving-text {
  margin-top: 4px;
  font-size: 12px;
}

@media (max-width: 600px) {
  .holiday-approval-header {
    display: block;
  }

  .holiday-approval-card .card-title {
    font-size: 1.45rem;
    line-height: 1.25;
  }

  .holiday-requests-table-wrap {
    margin-top: 12px;
  }

  .holiday-requests-table {
    min-width: 820px;
  }

  .approval-options {
    gap: 10px;
  }

  .approval-option {
    min-height: 32px;
  }
}
</style>
